import React, { useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import {
  tituloTabelaAddOrcamentoPorCarga,
  tituloTabelaAddOrcamentoPorServico,
  tituloTabelaAddTomadaPorCarga,
} from "../../../static";
import { formatCurrency, parseValue } from "../../../services/utils";

const FaseTwoTabelaItens = ({ slug, rows = [], isFornecedor = false, onRowChange }) => {
  // Resolve headers dynamically
  const selectedHeaders = useMemo(() => {
    if (tituloTabelaAddOrcamentoPorCarga[slug]) {
      return tituloTabelaAddOrcamentoPorCarga[slug];
    }
    if (tituloTabelaAddOrcamentoPorServico[slug]) {
      return tituloTabelaAddOrcamentoPorServico[slug];
    }
    // Fallback/Merge with Phase 1 titles for custom columns (e.g. medicao_de_sistemas_e_insumos)
    if (tituloTabelaAddTomadaPorCarga[slug]) {
      return {
        ...(tituloTabelaAddOrcamentoPorServico.servico || {}),
        ...tituloTabelaAddTomadaPorCarga[slug],
      };
    }
    return tituloTabelaAddOrcamentoPorServico.servico || {};
  }, [slug]);

  // Check if a header represents a boolean (contains "?" or "S/N")
  const isBooleanColumn = (label) => {
    if (!label) return false;
    const lower = label.toLowerCase();
    return lower.includes("?") || lower.includes("s/n");
  };

  // Build the list of columns to render in a clean, standard order
  const columnsToRender = useMemo(() => {
    const cols = [];

    // 1. Fabricante/Modelo/Discriminação
    if (selectedHeaders.colunaA1) {
      cols.push({ key: "colunaA1", label: selectedHeaders.colunaA1, editable: true });
    }

    // 2. Specifications from Phase 1 (coluna1 to coluna13) - Read-only in Phase 2
    for (let i = 1; i <= 13; i++) {
      const key = `coluna${i}`;
      if (selectedHeaders[key]) {
        cols.push({ key, label: selectedHeaders[key], editable: false });
      }
    }

    // 3. Specifications from Phase 2 (colunaB1 to colunaB12) - Editable for supplier
    for (let i = 1; i <= 12; i++) {
      const key = `colunaB${i}`;
      if (selectedHeaders[key]) {
        cols.push({ key, label: selectedHeaders[key], editable: true });
      }
    }

    // 4. Custom service columns (coluna18 to coluna22) - Read-only
    for (let i = 18; i <= 22; i++) {
      const key = `coluna${i}`;
      if (selectedHeaders[key]) {
        cols.push({ key, label: selectedHeaders[key], editable: false });
      }
    }

    // 5. Quantidade vem da 1a fase; o fornecedor pode corrigir o valor unitario na 2a fase.
    if (selectedHeaders.coluna14) {
      cols.push({ key: "coluna14", label: selectedHeaders.coluna14, editable: false });
    }
    if (selectedHeaders.coluna15) {
      cols.push({ key: "coluna15", label: selectedHeaders.coluna15, editable: true, isCurrency: true });
    }
    if (selectedHeaders.coluna16) {
      cols.push({ key: "coluna16", label: selectedHeaders.coluna16, editable: false, isCurrency: true });
    }

    // 6. Installation, Disposal, Row Total (colunaB13, colunaB14, colunaB15) - Installation/Disposal editable
    if (selectedHeaders.colunaB13) {
      cols.push({ key: "colunaB13", label: selectedHeaders.colunaB13, editable: true, isCurrency: true });
    }
    if (selectedHeaders.colunaB14) {
      cols.push({ key: "colunaB14", label: selectedHeaders.colunaB14, editable: true, isCurrency: true });
    }
    if (selectedHeaders.colunaB15) {
      cols.push({ key: "colunaB15", label: selectedHeaders.colunaB15, editable: false, isCurrency: true, isComputedRowTotal: true });
    }

    return cols;
  }, [selectedHeaders]);

  // Compute footer totals dynamically
  const totals = useMemo(() => {
    const validRows = Array.isArray(rows) ? rows : [];
    return validRows.reduce(
      (acc, row) => {
        const col14 = parseValue(row.coluna14);
        const col15 = parseValue(row.coluna15);
        const col16 = parseValue(row.coluna16);
        const colB13 = parseValue(row.colunaB13);
        const colB14 = parseValue(row.colunaB14);
        const colB15 = col16 + colB13 + colB14;

        return {
          coluna14: acc.coluna14 + col14,
          coluna15: acc.coluna15 + col15,
          coluna16: acc.coluna16 + col16,
          colunaB13: acc.colunaB13 + colB13,
          colunaB14: acc.colunaB14 + colB14,
          colunaB15: acc.colunaB15 + colB15,
        };
      },
      { coluna14: 0, coluna15: 0, coluna16: 0, colunaB13: 0, colunaB14: 0, colunaB15: 0 }
    );
  }, [rows]);

  const renderCellContent = (row, index, col) => {
    const value = row[col.key];
    const isBool = isBooleanColumn(col.label);
    const isEdit = col.editable && isFornecedor;

    if (isEdit) {
      if (isBool) {
        return (
          <Select
            error={value === "" || value == null}
            value={value ?? ""}
            onChange={(e) => onRowChange(index, col.key, e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value="">Selecione</MenuItem>
            <MenuItem value="Sim">Sim</MenuItem>
            <MenuItem value="Não">Não</MenuItem>
          </Select>
        );
      } else {
        const isNumOrCurrency = col.isCurrency || col.key === "coluna14";
        return (
          <TextField
            error={value === "" || value == null}
            helperText={value === "" ? "Campo obrigatório" : ""}
            value={value ?? ""}
            type={isNumOrCurrency ? "number" : "text"}
            inputProps={isNumOrCurrency ? { min: 0 } : undefined}
            variant="outlined"
            size="small"
            fullWidth
            onChange={(e) => onRowChange(index, col.key, e.target.value)}
          />
        );
      }
    } else {
      if (col.isCurrency || col.isComputedRowTotal) {
        return formatCurrency(value || 0);
      }
      return value ?? "";
    }
  };

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader sx={{ minWidth: Math.max(900, columnsToRender.length * 140) }}>
        <TableHead>
          <TableRow>
            {columnsToRender.map((col) => (
              <TableCell key={col.key} align="center" sx={{ minWidth: 120 }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {columnsToRender.map((col) => (
                <TableCell key={col.key} align="center" sx={{ minWidth: 120, whiteSpace: "nowrap" }}>
                  {renderCellContent(row, index, col)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              align="right"
              colSpan={columnsToRender.filter((c) => !["coluna14", "coluna15", "coluna16", "colunaB13", "colunaB14", "colunaB15"].includes(c.key)).length}
            >
              Totais
            </TableCell>
            {columnsToRender
              .filter((c) => ["coluna14", "coluna15", "coluna16", "colunaB13", "colunaB14", "colunaB15"].includes(c.key))
              .map((col) => {
                const totalVal = totals[col.key];
                return (
                  <TableCell key={col.key} align="center">
                    {col.key === "coluna14" ? totalVal : formatCurrency(totalVal)}
                  </TableCell>
                );
              })}
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default FaseTwoTabelaItens;
